import dayjs, { Dayjs } from "dayjs";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { TSearchRsoLearnerReqDto } from "@/api/services/main/rso-learner/dtos/search-rso-learner.dto";
import { toSkillOnePeriodMonths } from "@/features/learner/components/learner-list/bulk-sync-filter-schema";

// irohana visa 取り込みモーダルの「フィルタ」パネルが扱う検索条件。
// 値は確定APIへ繋ぐ前提のため、入力系=文字列 / 選択系=ID文字列 / 日付系=Dayjs|null で保持する。
// 選択肢(分野/国籍=master, 在籍状況/性別=enum)は useBulkSyncFilterMasterData が提供する。
export type TBulkSyncFilterForm = {
  managementNumber: string; // 管理番号
  employmentStatus: string[]; // 在籍状況 (複数選択)
  companyName: string; // 所属機関 (RSOのみ)
  fullName: string; // 外国人名
  industryNames: string[]; // 分野 (名称で検索・複数選択)
  nationalityNames: string[]; // 国籍 (名称で検索・複数選択)
  gender: string; // 性別
  hiringDateFrom: Dayjs | null; // 入社日 (from)
  hiringDateTo: Dayjs | null; // 入社日 (to)
  residenceExpiryFrom: Dayjs | null; // 在留期限 (from)
  residenceExpiryTo: Dayjs | null; // 在留期限 (to)
  skillOnePeriodYearFrom: string; // 1号経過期間 from: 年 (≤ 4)
  skillOnePeriodMonthFrom: string; // 1号経過期間 from: 月 (≤ 11)
  skillOnePeriodYearTo: string; // 1号経過期間 to: 年 (≤ 4)
  skillOnePeriodMonthTo: string; // 1号経過期間 to: 月 (≤ 11)
};

export const EMPTY_BULK_SYNC_FILTER: TBulkSyncFilterForm = {
  managementNumber: "",
  employmentStatus: [],
  companyName: "",
  fullName: "",
  industryNames: [],
  nationalityNames: [],
  gender: "",
  hiringDateFrom: null,
  hiringDateTo: null,
  residenceExpiryFrom: null,
  residenceExpiryTo: null,
  skillOnePeriodYearFrom: "",
  skillOnePeriodMonthFrom: "",
  skillOnePeriodYearTo: "",
  skillOnePeriodMonthTo: "",
};

// localStorage 保存キー (irohana visa 取り込みフィルタ)。ページ横断でないため industry スコープは持たない。
export const BULK_SYNC_FILTER_STORAGE_KEY = "bulk_sync_learner_filter";

// 日付レンジのキー (localStorage 復元時に Dayjs へ再水和する対象)。
const BULK_SYNC_DATE_KEYS: (keyof TBulkSyncFilterForm)[] = [
  "hiringDateFrom",
  "hiringDateTo",
  "residenceExpiryFrom",
  "residenceExpiryTo",
];

// 複数選択のキー (localStorage 復元時に常に文字列配列へ揃える対象)。
const BULK_SYNC_MULTI_KEYS: (keyof TBulkSyncFilterForm)[] = ["employmentStatus", "industryNames", "nationalityNames"];

// 1号経過期間 (年 / 月) のバリデーションは bulk-sync-filter-schema.ts を参照。

// 値が空か (未入力の文字列 / null 日付 / 空配列の複数選択)。
const isEmptyBulkSyncValue = (v: TBulkSyncFilterForm[keyof TBulkSyncFilterForm]): boolean =>
  v == null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

// パネルに条件が1つでも入っているか (クリアボタンの活性判定など)。
export const hasBulkSyncFilter = (f: TBulkSyncFilterForm): boolean =>
  Object.values(f).some((v) => !isEmptyBulkSyncValue(v));

// 適用中の条件数 (トリガーの「フィルタ適用数」表示用)。
// exam-steps / learning-progress と同様、日付レンジ (from + to) は1件として数える。
export const countBulkSyncFilter = (f: TBulkSyncFilterForm): number => {
  const {
    hiringDateFrom,
    hiringDateTo,
    residenceExpiryFrom,
    residenceExpiryTo,
    skillOnePeriodYearFrom,
    skillOnePeriodMonthFrom,
    skillOnePeriodYearTo,
    skillOnePeriodMonthTo,
    ...rest
  } = f;
  let count = Object.values(rest).filter((v) => !isEmptyBulkSyncValue(v)).length;
  if (hiringDateFrom || hiringDateTo) count += 1;
  if (residenceExpiryFrom || residenceExpiryTo) count += 1;
  // 1号経過期間は from/to (年/月) の複数入力だが1条件として数える。
  if (skillOnePeriodYearFrom.trim() || skillOnePeriodMonthFrom.trim() || skillOnePeriodYearTo.trim() || skillOnePeriodMonthTo.trim()) {
    count += 1;
  }
  return count;
};

// パネルの確定条件を visa 検索API (searchRsoLearnersApi) のクエリへ変換する。
// 空値は送らない / ID系は number / 日付は API 用フォーマット(YYYY-MM-DD)へ整形する。
// 追加フィールド名は現行DTO規約に合わせた暫定値 (BE契約が判明したら要確認)。
export const buildBulkSyncSearchParams = (
  f: TBulkSyncFilterForm,
  isCompanyUser: boolean,
): Partial<TSearchRsoLearnerReqDto> => {
  const params: Partial<TSearchRsoLearnerReqDto> = {};
  const fmt = (d: Dayjs | null) => (d ? dayjs(d).format(DATE_FORMAT_FOR_API) : undefined);

  if (f.managementNumber.trim()) params.managementNumber = f.managementNumber.trim();
  if (f.employmentStatus.length) params.employmentStatus = f.employmentStatus;
  // 会社アカウントは自社の外国人のみ扱うため所属機関条件は送らない。
  if (!isCompanyUser && f.companyName.trim()) params.companyName = f.companyName.trim();
  if (f.fullName.trim()) params.fullName = f.fullName.trim();
  // 分野・国籍は名称 (industryNames / nationalityNames) で検索する。
  if (f.industryNames.length) params.industryNames = f.industryNames;
  if (f.nationalityNames.length) params.nationalityNames = f.nationalityNames;
  if (f.gender.trim()) params.gender = f.gender.trim();
  if (f.hiringDateFrom) params.hiringDateFrom = fmt(f.hiringDateFrom);
  if (f.hiringDateTo) params.hiringDateTo = fmt(f.hiringDateTo);
  if (f.residenceExpiryFrom) params.residenceExpiryDateFrom = fmt(f.residenceExpiryFrom);
  if (f.residenceExpiryTo) params.residenceExpiryDateTo = fmt(f.residenceExpiryTo);
  // 1号経過期間: from/to をそれぞれ合計月数 (年*12 + 月) へ変換し、月レンジとして送る。
  // 変換ロジックは bulk-sync-filter-schema の toSkillOnePeriodMonths を再利用する (DRY)。
  const skillOnePeriodMonthsFrom = toSkillOnePeriodMonths(f.skillOnePeriodYearFrom, f.skillOnePeriodMonthFrom);
  const skillOnePeriodMonthsTo = toSkillOnePeriodMonths(f.skillOnePeriodYearTo, f.skillOnePeriodMonthTo);
  if (skillOnePeriodMonthsFrom != null) params.skillOnePeriodMonthsFrom = skillOnePeriodMonthsFrom;
  if (skillOnePeriodMonthsTo != null) params.skillOnePeriodMonthsTo = skillOnePeriodMonthsTo;
  return params;
};

// localStorage 保存用ペイロードへ変換する。空値は除外し、日付は文字列(YYYY-MM-DD)へ整形する。
const serializeBulkSyncFilter = (f: TBulkSyncFilterForm): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(f) as [keyof TBulkSyncFilterForm, TBulkSyncFilterForm[keyof TBulkSyncFilterForm]][]) {
    if (isEmptyBulkSyncValue(value)) continue;
    out[key] = dayjs.isDayjs(value) ? value.format(DATE_FORMAT_FOR_API) : value;
  }
  return out;
};

// localStorage の JSON 文字列を TBulkSyncFilterForm へ復元する。日付は Dayjs へ再水和する。
// 不正・未保存時は空フィルタを返す。
const hydrateBulkSyncFilter = (raw: string | null): TBulkSyncFilterForm => {
  if (!raw) return { ...EMPTY_BULK_SYNC_FILTER };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { ...EMPTY_BULK_SYNC_FILTER };
    const source = parsed as Partial<Record<keyof TBulkSyncFilterForm, unknown>>;
    const result: TBulkSyncFilterForm = { ...EMPTY_BULK_SYNC_FILTER };
    for (const key of Object.keys(EMPTY_BULK_SYNC_FILTER) as (keyof TBulkSyncFilterForm)[]) {
      const value = source[key];
      if (value === undefined || value === null) continue;
      if (BULK_SYNC_DATE_KEYS.includes(key)) {
        const day = dayjs(value as string);
        (result[key] as Dayjs | null) = day.isValid() ? day : null;
      } else if (BULK_SYNC_MULTI_KEYS.includes(key)) {
        // 複数選択 (在籍状況 / 分野 / 国籍) は常に文字列配列。旧データ(単一文字列)は配列へ移行する。
        (result[key] as string[]) = Array.isArray(value)
          ? value.filter((v): v is string => typeof v === "string")
          : typeof value === "string"
            ? [value]
            : [];
      } else if (typeof value === "string") {
        (result[key] as string) = value;
      }
    }
    return result;
  } catch {
    return { ...EMPTY_BULK_SYNC_FILTER };
  }
};

// localStorage からフィルタを読み込む (初期化・再オープン時)。
export const loadBulkSyncFilter = (): TBulkSyncFilterForm => {
  if (typeof window === "undefined") return { ...EMPTY_BULK_SYNC_FILTER };
  return hydrateBulkSyncFilter(window.localStorage.getItem(BULK_SYNC_FILTER_STORAGE_KEY));
};

// フィルタを localStorage へ保存する。空 (条件なし) の場合はキーごと削除する。
export const persistBulkSyncFilter = (f: TBulkSyncFilterForm): void => {
  if (typeof window === "undefined") return;
  if (hasBulkSyncFilter(f)) {
    window.localStorage.setItem(BULK_SYNC_FILTER_STORAGE_KEY, JSON.stringify(serializeBulkSyncFilter(f)));
  } else {
    window.localStorage.removeItem(BULK_SYNC_FILTER_STORAGE_KEY);
  }
};
